import { useMutation, useSubscription } from '@apollo/client';
import { Box, Button, List } from '@mui/material';
import { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NegotiationCaseCreateWizardState } from '../NegotiationCaseCreateWizard/NegotiationCaseCreateWizard.schema';
import { SvbrgBackgroundJobStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ProgressIndicatorAlternating } from '~/components/ProgressIndicator/ProgressIndicatorAlternating';
import { useWizardState } from '~/components/WizardState/hooks/useWizardState/useWizardState';
import { WizardRef } from '~/components/WizardState/WizardState';
import { useDemandDocuments } from '~/hooks';
import { DocumentListItem } from '~/modules/demand/DemandDocumentWidget/components/DocumentListItem/DocumentListItem';
import { DocumentListSkeleton } from '~/modules/demand/DemandDocumentWidget/components/DocumentListSkeleton/DocumentListSkeleton';
import { createUploadIndicatorAlternatingProps } from '~/routes/IntakeDetail/steps/IntakeQuoteUploadStep/IntakeQuoteUploadStep.settings';

import { ANALYSE_QUOTE_MUTATION } from './AnalyseQuote.mutation.gql';
import { QUOTE_ANALYSIS_SUBSCRIPTION } from './AnalyseQuote.subscription.gql';

export const SelectQuote = forwardRef<WizardRef, any>((_props, _ref) => {
  const { nextStep, updateState, onError } = useWizardState<NegotiationCaseCreateWizardState>();

  const { files, loading } = useDemandDocuments();

  const [analysisId, setAnalysisId] = useState<string | undefined>();
  const [analyseQuote] = useMutation(ANALYSE_QUOTE_MUTATION);

  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'DialogWizard.general.actions' });
  const { t: tIntake } = useTranslation(I18nNamespace.intake);

  const handleExtractedQuote = useCallback(
    ({ data: subscription }: any) => {
      const analysis = subscription.data?.sevenbridges?.quote?.analysis;
      if (analysis?.status === SvbrgBackgroundJobStatus.FAILED) {
        onError({ message: tIntake('errors.analysisFailed') });
        setAnalysisId(undefined);
        return;
      }
      if (analysis?.status === SvbrgBackgroundJobStatus.COMPLETED) {
        updateState({
          fileId: analysis?.fileId,
          quote: analysis?.quote,
        });
        nextStep();
      }
    },
    [nextStep, onError, tIntake, updateState],
  );

  useSubscription(QUOTE_ANALYSIS_SUBSCRIPTION, {
    variables: { id: analysisId },
    skip: !analysisId,
    fetchPolicy: 'no-cache',
    onData: handleExtractedQuote,
    onError,
  });

  const startAnalysis = async (fileId: string, fileName: string): Promise<string | undefined> => {
    const analysis = await analyseQuote({ variables: { data: { fileId, fileName } } });
    return analysis.data?.sevenbridges.quote.analyse.id;
  };

  return analysisId ? (
    <Box sx={{ '& > div': { justifyContent: 'center' } }}>
      <ProgressIndicatorAlternating {...createUploadIndicatorAlternatingProps(tIntake)} progressBarWidth="30%" />
    </Box>
  ) : (
    <List disablePadding>
      {loading ? (
        <DocumentListSkeleton />
      ) : (
        files.map((file) => (
          <DocumentListItem
            key={file.id}
            file={file}
            analyseDocumentAction={
              <Button
                size="small"
                onClick={async () => {
                  // File name is set to empty string because it is not used in the backend
                  // But it is required to be passed as a parameter due to the intake process
                  setAnalysisId(await startAnalysis(file.id, ''));
                }}
              >
                {t('select')}
              </Button>
            }
          />
        ))
      )}
    </List>
  );
});
